import { AnnotationContextProps, Box, TutorialPanelProps } from "@cloudscape-design/components";
import React, { useCallback, useState } from "react";
import { TutorialInfoTopic } from "./store";
import { TutorialHotspot } from "./constants";

export enum TutorialType {
  MANAGE_CAMPAIGN,
  CREATE_FLIGHT_PLAN,
}


/**
 * Content for tutorials
 */
export const tutorialData: (
  helpPanelHandler: (activeTopic: TutorialInfoTopic) => () => void
  ) => TutorialPanelProps.Tutorial[] = () => [
    {
      id: TutorialType.MANAGE_CAMPAIGN,
      title: "Learn about TaCS campaigns",
      completed: false,
      description: (
        <>
          <Box variant="p" color="text-body-secondary" padding={{top: "n"}}
          >
            In this tutorial, you will:
          </Box>
          <ul>
            <li>
              <Box variant="span" color="text-body-secondary">
                Familiarize yourself with campaign basic UI elements.
              </Box>
            </li>
            <li>
              <Box variant="span" color="text-body-secondary">
                See how to view campaigns; filter campaigns based on desired properties.
              </Box>
            </li>
            <li>
              <Box variant="span" color="text-body-secondary">
                See where to choose and select what properties of campaigns to display.
              </Box>
            </li>
          </ul>
        </>
      ),
      learnMoreUrl: "https://w.amazon.com/bin/view/MODE/Data_Technology/Products/TaCS/",
      completedScreenDescription: "You've completed tutorial about managing campaigns on MODE web page.",
      tasks: [
        {
          title: "Managing campaigns",
          steps: [
            {
              title: "Manage campaign page",
              content: (
                <Box variant="p">
                  On this page, you can manage existing campaigns, create new campaigns, edit, or download bulk 
                  campaigns. You can also use the Action button to achive a campaign or a list of selected campaigns.
                </Box>
              ),
              hotspotId: TutorialHotspot.manageCampaign
            },
            {
              title: "Create new campaign",
              content: (
                <>
                  <Box variant="p">
                    Use the create campaign button to create new campaigns. After creating a new campaign
                    with the required fields, you are able to return back to update or add additional details
                    to the created campaign.
                  </Box>
                </>
              ),
              hotspotId: TutorialHotspot.createCampaignBtn
            },
            {
              title: "Filter campaigns to view",
              content: (
                <Box variant="p">
                  Filter what campaign to view in the table {" "}
                  With the property filter, users can find specific items in a collection of resources
                  by using properties, property values, typeing free text, and combining these with operators.
                </Box>
              ),
              hotspotId: TutorialHotspot.filterCampaignItems,
            },
            {
              title: "Campaign display options",
              content: (
                <Box variant="p">
                  Use the Settings to choose what properties of campaigns to view in the table
                </Box>
              ),
              hotspotId: TutorialHotspot.paginationAndDisplay
            },
          ]
        },
      ],
    },
    //Add additional tutorials
  ];

export const useAvailableTutorials = (helpPanelHandler: (topic: TutorialInfoTopic) => () => void) => {
  const [tutorials, setTutorials] = useState(() => 
    tutorialData(helpPanelHandler).map(tutorial => ({
      ...tutorial,
      completed: false,
    }))
  );

  const setTutorialCompleted = useCallback((tutorial: TutorialPanelProps.Tutorial, completed: boolean) => {
    setTutorials(tutorials => {
      return tutorials.map((t: TutorialPanelProps.Tutorial) => {
        return { ...t, completed};
      });
    });
  }, []);

  return { tutorials, setTutorialCompleted };
};

export const overlayI18nStrings: AnnotationContextProps.I18nStrings = {
  stepCounterText: (stepIndex: number, totalStepCount: number) => (
    "Step " + (stepIndex + 1) + "/" + totalStepCount
  ),
  taskTitle: (taskIndex: number, taskTitle: string) => (
    "Task " + (taskIndex + 1) + ": " + taskTitle
  ),
  labelHotspot(openState: boolean, stepIndex: number, totalStepCount: number) {
    return openState
      ? "close annotation for step " + (stepIndex + 1) + " of " + totalStepCount
      : "open annotation";
  },
  nextButtonText: "Next",
  previousButtonText: "Previous",
  finishButtonText: "Finish",
  labelDismissAnnotation: "Dismiss annotation",
};

export const tutorialPanelI18nStrings = {
  loadingText: "Loading",
  tutorialListTitle: "Choose a tutorial",
  tutorialListDescription: "Use our walk-through tutorials to learn how to achieve your desired objectives within TaCS." ,
  tutorialListDownloadLinkText: "Placeholder - TaCS wiki",
  tutorialCompletedText: "Tutorial completed",
  labelExitTutorial: "dismiss tutorial",
  learnMoreLinkText: "Learn more",
  startTutorialButtonText: "Start tutorial",
  restartTutorialButtonText: "Restart tutorial",
  completionScreenTitle: "Congratulations! You completed the tutorial.",
  dismissTutorialButtonText: "Dismiss tutorial",
  taskTitle: (taskIndex: number, taskTitle: string) => `Task ${taskIndex + 1}: ${taskTitle}`,
  stepTitle: (stepIndex: number, stepTitle: string) => `Step ${stepIndex + 1}: ${stepTitle}`,
  labelTotalSteps: (totalStepCount: number) => `Total steps: ${totalStepCount}`,
  labelLearnMoreExternalIcon: "Learn more about this tutorial",
  labelsTaskStatus: { pending: "Pending", "in-progress": "In-progress", success: "Sucess", }
} as TutorialPanelProps.I18nStrings;
